import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, Check, TrendingUp, Shield, Zap, BarChart3, Activity, Brain } from 'lucide-react';
import { Button } from '../components/ui/button';
import InteractiveBg from '../components/InteractiveBg';
import HeroVisual from '../components/HeroVisual';
import TrustpilotBadge from '../components/TrustpilotBadge';
import TradingWidget from '../components/TradingWidget';
import AIWidget from '../components/AIWidget';
import { useNavigate } from 'react-router-dom';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import { pricingPlans } from '../data/mockData';

const useCounter = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!started) return;
    let v = 0;
    const step = end / (duration / 16);
    const t = setInterval(() => {
      v += step;
      if (v >= end) { setCount(end); clearInterval(t); }
      else setCount(Math.floor(v));
    }, 16);
    return () => clearInterval(t);
  }, [started, end, duration]);

  return { count, ref };
};

const FadeIn = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const traders = useCounter(66000, 2200);
  const winRate = useCounter(89, 1800);
  const markets = useCounter(150, 1600);

  
  const [typedText, setTypedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  useEffect(() => {
    const fullText = 'Profits with AI';
    let i = 0;
    const typeTimer = setInterval(() => {
      if (i <= fullText.length) {
        setTypedText(fullText.slice(0, i));
        i++;
      } else {
        clearInterval(typeTimer);
        
        setTimeout(() => setShowCursor(false), 2000);
      }
    }, 80);
    
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => { clearInterval(typeTimer); clearInterval(cursorTimer); };
  }, []);

  return (
    <div className="min-h-screen bg-[#111113] relative">
            <InteractiveBg />

            <div className="relative z-10">

            <section className="relative pt-24 pb-16 md:pt-36 lg:pt-44 md:pb-24 lg:pb-32 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-14 lg:gap-20 items-center">
                        <div className="space-y-6 md:space-y-7">
                            <FadeIn>
                <div className="flex justify-center lg:justify-start">
                  <TrustpilotBadge />
                </div>
              </FadeIn>

              <FadeIn delay={100}>
                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[80px] font-bold text-white leading-[1.05] tracking-tight">
                  <span className="hero-word" style={{ animationDelay: '0s' }}>
                    <span className="text-[#38FE60]">Automate</span> Your
                  </span>
                  <br />
                  <span className="hero-word" style={{ animationDelay: '0.15s' }}>
                    Trades &amp; <span className="text-[#38FE60]">Maximize</span>
                  </span>
                  <br />
                  <span className="hero-word" style={{ animationDelay: '0.3s' }}>
                    {typedText}<span className={`inline-block w-[3px] h-[0.85em] bg-[#38FE60] ml-1 align-middle ${showCursor ? 'opacity-100' : 'opacity-0'}`} />
                  </span>
                </h1>
              </FadeIn>

              <FadeIn delay={200}>
                <p className="text-[#999] text-base md:text-lg lg:text-xl leading-relaxed max-w-lg">
                  Unlock powerful trading AI bots, Indicators, Signals, and Alerts used by{' '}
                  <span className="text-white font-semibold" ref={traders.ref}>{traders.count.toLocaleString()}+</span>{' '}
                  traders globally.
                </p>
              </FadeIn>

                            <FadeIn delay={300}>
                <div className="flex flex-wrap gap-6 py-1">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#38FE60] rounded-full animate-pulse"></div>
                    <span className="text-[#888] text-sm"><span className="text-white font-medium">{traders.count.toLocaleString()}+</span> Traders</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-3.5 h-3.5 text-[#38FE60]" />
                    <span className="text-[#888] text-sm" ref={winRate.ref}><span className="text-white font-medium">{winRate.count}%</span> Win Rate</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-3.5 h-3.5 text-[#38FE60]" />
                    <span className="text-[#888] text-sm" ref={markets.ref}><span className="text-white font-medium">{markets.count}+</span> Markets</span>
                  </div>
                </div>
              </FadeIn>

              <FadeIn delay={400}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-1">
                  <Button
                    onClick={() => navigate('/pricing')}
                    className="hero-cta-btn bg-[#38FE60] text-[#111113] hover:bg-[#2ee055] font-semibold px-8 py-6 rounded-full text-base md:text-lg transition-all hover:scale-[1.03] w-full sm:w-auto"
                  >
                    Get Access Now
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#38FE60]/60" />
                    <span className="text-[#666] text-xs md:text-sm">No trading experience required</span>
                  </div>
                </div>
              </FadeIn>
            </div>

                        <FadeIn delay={200}>
              <div className="relative mt-6 lg:mt-0 h-[350px] md:h-[420px] lg:h-[480px]">
                <HeroVisual />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

            <section className="py-8 md:py-10 border-y border-[#ffffff08] overflow-hidden">
        <div className="relative">
          <div className="flex animate-scroll-fast">
            {[1,2,3].map(set => (
              <div key={set} className="flex items-center justify-around min-w-max space-x-24 md:space-x-32 lg:space-x-40 px-12 md:px-16">
                <img src="/businessinsider.webp" alt="Business Insider" className="h-7 md:h-9 lg:h-10 w-auto object-contain opacity-50 hover:opacity-100 transition-opacity" style={{ minWidth: '100px' }} />
                <img src="/marketwatch.webp" alt="MarketWatch" className="h-7 md:h-9 lg:h-10 w-auto object-contain opacity-50 hover:opacity-100 transition-opacity" style={{ minWidth: '100px' }} />
                <img src="/yahoo.webp" alt="Yahoo Finance" className="h-7 md:h-9 lg:h-10 w-auto object-contain opacity-50 hover:opacity-100 transition-opacity" style={{ minWidth: '100px' }} />
                <img src="/barchart.webp" alt="Barchart" className="h-7 md:h-9 lg:h-10 w-auto object-contain opacity-50 hover:opacity-100 transition-opacity" style={{ minWidth: '100px' }} />
                <img src="/forbes.webp" alt="Forbes" className="h-7 md:h-9 lg:h-10 w-auto object-contain opacity-50 hover:opacity-100 transition-opacity" style={{ minWidth: '100px' }} />
              </div>
            ))}
          </div>
        </div>
      </section>

            <section className="py-20 md:py-28 lg:py-36">
        <div className="container mx-auto px-4 md:px-6">
          <FadeIn>
            <div className="bg-gradient-to-br from-[#141416] to-[#0c0c0e] rounded-2xl md:rounded-3xl p-6 md:p-10 lg:p-14 border border-[#ffffff08]">
              <div className="grid lg:grid-cols-[45%_55%] gap-8 md:gap-12 lg:gap-16 items-center">
                <div className="space-y-5">
                  <span className="text-[#38FE60] text-xs md:text-sm tracking-widest uppercase">
                    Advanced <span className="text-white">Toolkits</span>
                  </span>
                  <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                    <span className="text-[#38FE60]">Next-Generation</span>
                    <br />
                    <span className="text-white">Trading Solutions</span>
                  </h2>
                  <p className="text-[#888] text-sm md:text-base lg:text-lg leading-relaxed">
                    Our revolutionary algorithms, designed for both automated and manual trading, empower investors of all levels—from beginners to seasoned experts—to succeed in any financial market. Trusted by over 66,000 traders.
                  </p>
                </div>
                <div className="relative w-full aspect-video rounded-xl md:rounded-2xl overflow-hidden lg:-ml-4">
                  <video className="w-full h-full object-cover" autoPlay loop muted playsInline>
                    <source src="/IMG_0689.MP4" type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

            <section className="py-20 md:py-28 lg:py-36">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-14 lg:gap-20 items-center">
                        <FadeIn className="order-2 lg:order-1">
              <div className="h-[400px] md:h-[460px] lg:h-[500px] hover:scale-[1.01] transition-transform duration-500">
                <TradingWidget />
              </div>
            </FadeIn>

                        <FadeIn className="order-1 lg:order-2" delay={100}>
              <div className="space-y-5">
                <span className="text-[#38FE60] text-xs font-medium uppercase tracking-widest bg-[#38FE60]/5 px-4 py-1.5 rounded-full border border-[#38FE60]/10 inline-block">
                  Master Suite
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Loaded with
                  <br />
                  Powerful Insights
                </h2>
                <p className="text-[#888] text-sm md:text-base lg:text-lg leading-relaxed">
                  Effortlessly identify high-probability setups while avoiding high-risk trades. Get professional-grade alerts that integrate seamlessly with our toolkits for precision and confidence.
                </p>
                <div className="grid grid-cols-2 gap-2.5 pt-2">
                  {['Real-time Alerts', 'Buy/Sell Signals', 'Trend Detection', 'Risk Management'].map(f => (
                    <div key={f} className="flex items-center gap-2 bg-[#ffffff04] rounded-lg px-3 py-2.5 border border-[#ffffff06]">
                      <Check className="w-3.5 h-3.5 text-[#38FE60] flex-shrink-0" />
                      <span className="text-[#999] text-xs">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

            <section className="py-20 md:py-28 lg:py-36">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-14 lg:gap-20 items-center">
                        <FadeIn>
              <div className="space-y-5">
                <span className="text-[#38FE60] text-xs font-medium uppercase tracking-widest bg-[#38FE60]/5 px-4 py-1.5 rounded-full border border-[#38FE60]/10 inline-block">
                  AI Engine
                </span>
                <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                  Unleash The Power of
                  <br />
                  Artificial Intelligence
                </h2>
                <p className="text-[#888] text-sm md:text-base lg:text-lg leading-relaxed">
                  Our AI neural network processes millions of data points in real-time — analyzing price action, volume patterns, and market sentiment to generate high-confidence trading signals.
                </p>
                <div className="space-y-3 pt-2">
                  {[
                    { icon: Brain, title: 'Neural Network Analysis', desc: 'Multi-layer deep learning trained on historical market data' },
                    { icon: TrendingUp, title: 'Predictive Signals', desc: 'AI-generated buy, hold, and sell signals with confidence scores' },
                  ].map(({ icon: Icon, title, desc }) => (
                    <div key={title} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#38FE60]/5 border border-[#38FE60]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-[#38FE60]" />
                      </div>
                      <div>
                        <span className="text-white text-sm font-medium">{title}</span>
                        <p className="text-[#666] text-xs mt-0.5">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>

                        <FadeIn delay={100}>
              <div className="h-[400px] md:h-[460px] lg:h-[500px] hover:scale-[1.01] transition-transform duration-500">
                <AIWidget />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

            <section className="py-20 md:py-28 lg:py-36">
        <div className="container mx-auto px-4 md:px-6">
          <FadeIn>
            <div className="text-center mb-14 md:mb-18">
              <div className="inline-block bg-[#38FE60] text-[#111113] px-5 py-1.5 rounded-full font-semibold text-xs mb-7">
                Pricing
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 px-4">
                Get Access To The
                <br />
                <span className="text-[#38FE60]">KiteKraken Signals Toolkit</span>
              </h2>
              <p className="text-[#888] text-sm md:text-base lg:text-lg max-w-2xl mx-auto">
                Transparent pricing, no hidden fees.
              </p>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7 items-stretch">
            {pricingPlans.map((plan, i) => (
              <FadeIn key={plan.id} delay={i * 100} className="flex flex-col h-full">
                <div
                  className={`bg-gradient-to-b from-[#1a1c1e] to-[#141416] border rounded-2xl p-7 relative transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:border-[#38FE60]/30 flex flex-col h-full ${
                    plan.popular ? 'border-[#38FE60]/40 shadow-[0_0_30px_rgba(56,254,96,0.08)]' : 'border-[#ffffff08]'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <div className="bg-[#38FE60] text-[#111113] px-4 py-1 rounded-full text-[11px] font-semibold">
                        {plan.badge}
                      </div>
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                    <p className="text-[#888] text-sm mb-5">{plan.description}</p>
                    <div className="mb-1">
                      <span className="text-2xl text-[#555] line-through font-semibold">${plan.originalPrice}</span>
                    </div>
                    <div className="flex items-baseline justify-center gap-1.5">
                      <span className="text-5xl font-extrabold text-[#38FE60]">${plan.price}</span>
                      <span className="text-[#777] text-sm font-semibold">/{plan.duration}</span>
                    </div>
                    <div className="mt-3">
                      <span className="inline-block bg-[#38FE60]/8 text-[#38FE60] px-3 py-1 rounded-full text-[11px] font-bold border border-[#38FE60]/10">
                        Save ${plan.originalPrice - plan.price}
                      </span>
                    </div>
                  </div>
                  <ul className="space-y-2.5 mb-7 flex-1">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-sm">
                        <Check className="w-4 h-4 text-[#38FE60] flex-shrink-0 mt-0.5" />
                        <span className="text-[#ccc] font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => navigate('/contact')}
                    className={`w-full py-5 rounded-xl font-bold transition-all ${
                      plan.popular
                        ? 'bg-[#38FE60] text-[#111113] hover:bg-[#2ee055] hover:scale-[1.02]'
                        : 'bg-[#38FE60]/8 text-[#38FE60] border border-[#38FE60]/20 hover:bg-[#38FE60] hover:text-[#111113]'
                    }`}
                  >
                    Get Started
                  </Button>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

            <TestimonialsCarousel />

            <section className="py-20 md:py-28 lg:py-36">
        <div className="container mx-auto px-4 md:px-6">
          <FadeIn>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                Frequently Asked
                <br />
                Questions
              </h2>
            </div>
          </FadeIn>

          <div className="max-w-3xl mx-auto space-y-2.5">
            {[
              { q: 'How can I automate Bitcoin options (FnO)?', a: 'Kitekraken.AI is specifically engineered for Bitcoin Options. Our software handles complex Greeks-based strategies and rule-based automation, allowing you to manage volatility 24/7 without manual intervention. Connect your Delta Exchange India account and enjoy auto trading (your account, your profit).' },
              { q: 'What is Kitekraken.AI exactly?', a: 'We are a software development company that provides automation tools for traders. Our software lets you connect strategies (TradingView) to various exchanges (your account) to execute trades automatically across Forex, stocks, commodities, and crypto.' },
              { q: 'Is Kitekraken.AI a broker?', a: 'No. Kitekraken.AI is a service provider. We do not take or hold your funds. You connect your existing brokerage or exchange accounts to our platform via secure API keys.' },
              { q: 'Does Kitekraken.AI manage my money?', a: 'No. We are a non-custodial software provider. Your funds stay in your own brokerage or exchange account (for example, Delta Exchange India, Binance, Interactive Brokers). We only send execution signals via API.' },
              { q: 'Do I need coding skills to use Kitekraken.AI?', a: 'Not necessarily. While we support advanced algorithmic execution, our TradingView integration is designed to be user-friendly, allowing you to automate trades using simple webhooks without writing complex code.' },
              { q: 'Is my API key safe with you?', a: 'Yes. We use industry-standard encryption to protect your data. We also recommend enabling withdrawal restrictions on your API keys so the software can only place trades and cannot withdraw funds.' },
              { q: 'Which assets can I automate through Kitekraken.AI?', a: 'Our software is built for versatility. You can automate strategies for crypto (Bitcoin options on Delta Exchange India, and crypto futures on selected exchanges through TradingView), traditional markets (stocks, forex, and commodities), and any Kitekraken indicator or strategy that can generate a TradingView alert.' },
              { q: 'How does "Rule-Based Trading" help me?', a: 'Rule-based automation removes emotional bias. The strategy executes exactly when parameters are met, helping you stay disciplined even in highly volatile markets.' },
              { q: 'How does the TradingView integration work?', a: 'Purchase any Kitekraken.AI TradingView indicator or strategy plan, then use TradingView\'s webhook feature to automate trading in your linked exchange (for example, Delta Exchange India, Binance) with low-latency execution.' },
              { q: 'How do I get help if my bot isn\'t firing?', a: 'We provide 24/7 technical support through our help desk and community channels like Discord and Telegram. You can also access documentation for step-by-step setup guides.' },
              { q: 'Is there a free trial?', a: 'We offer various tiers depending on your selected plan. Please visit our Pricing Page for details, or reach out on Telegram.' },
            ].map(({ q, a }, i) => (
              <FadeIn key={i} delay={i * 30}>
                <details className="group border border-[#ffffff06] rounded-xl overflow-hidden hover:border-[#38FE60]/20 transition-all duration-300"
                  style={{ background: 'rgba(12,12,14,0.4)' }}
                  onToggle={e => {
                    e.currentTarget.style.background = e.currentTarget.open
                      ? 'rgba(12,12,14,0.96)'
                      : 'rgba(12,12,14,0.4)';
                    e.currentTarget.style.borderColor = e.currentTarget.open
                      ? 'rgba(56,254,96,0.2)'
                      : 'rgba(255,255,255,0.06)';
                    e.currentTarget.style.boxShadow = e.currentTarget.open
                      ? '0 0 0 1px rgba(56,254,96,0.08), 0 8px 32px rgba(0,0,0,0.6)'
                      : 'none';
                  }}
                >
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer text-white font-medium text-sm md:text-base hover:text-[#38FE60] transition-colors select-none">
                    <span>{q}</span>
                    <span className="text-lg text-[#555] group-open:rotate-90 transition-transform duration-300 ml-4 flex-shrink-0">&#8250;</span>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5 text-[#aaa] text-sm leading-relaxed border-t border-[#ffffff06] pt-3">
                    {a}
                  </div>
                </details>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      </div>
    </div>
  );
};

export default Home;
